import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { createCustomer } from '../lib/data.js'

export default function CustomerNew() {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '',
    servicePlan: '', monthlyAmount: '', billingCycle: '1st',
    customSendDay: '', customDueDay: '',
    paymentMethod: 'stripe', notes: '',
  })

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const submit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) { setErr('Name is required.'); return }
    setBusy(true)
    setErr('')
    try {
      const id = await createCustomer({
        ...form,
        // createCustomer maps paymentMethod → autoDraft.
        monthlyAmount: Number(form.monthlyAmount) || 0,
      })
      navigate(`/customers/${id}`)
    } catch (e2) {
      setErr(e2?.message || 'Could not create customer.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-2xl">
      <Link to="/customers" className="text-sm text-brand-green-deep hover:underline">← All customers</Link>
      <h1 className="text-2xl font-extrabold mt-1">Add Customer</h1>

      <form onSubmit={submit} className="card mt-4 space-y-3">
        <Field label="Name *">
          <input autoFocus className="input" value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Email">
            <input type="email" className="input" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label="Phone">
            <input className="input" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
        </div>
        <Field label="Address">
          <input className="input" value={form.address} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Service plan">
            <input className="input" placeholder="e.g. 100 Mbps Fiber" value={form.servicePlan} onChange={(e) => set('servicePlan', e.target.value)} />
          </Field>
          <Field label="Monthly amount ($)">
            <input type="number" step="0.01" min="0" className="input" value={form.monthlyAmount} onChange={(e) => set('monthlyAmount', e.target.value)} />
          </Field>
          <Field label="Billing cycle">
            <select className="input" value={form.billingCycle} onChange={(e) => set('billingCycle', e.target.value)}>
              <option value="1st">1st of month</option>
              <option value="15th">15th of month</option>
              <option value="custom">Custom days</option>
            </select>
          </Field>
        </div>
        {form.billingCycle === 'custom' && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Send day (5–27)">
              <input type="number" min="5" max="27" className="input" value={form.customSendDay} onChange={(e) => set('customSendDay', e.target.value)} />
            </Field>
            <Field label="Due day (5–27)">
              <input type="number" min="5" max="27" className="input" value={form.customDueDay} onChange={(e) => set('customDueDay', e.target.value)} />
            </Field>
          </div>
        )}
        <Field label="Payment method">
          <div className="space-y-2">
            <PaymentMethodOption
              value="stripe"
              current={form.paymentMethod}
              onChange={(v) => set('paymentMethod', v)}
              title="Stripe Auto-Draft"
              blurb="We pull automatically each month — no customer action. Bank linked once via Stripe Financial Connections."
            />
            <PaymentMethodOption
              value="stripe_invoice"
              current={form.paymentMethod}
              onChange={(v) => set('paymentMethod', v)}
              title="Stripe Invoice (Pay button)"
              blurb="We email a monthly invoice with a Pay button. First time, the customer links their bank once (instant, no micro-deposits). After that it's one tap."
            />
            <PaymentMethodOption
              value="card_auto"
              current={form.paymentMethod}
              onChange={(v) => set('paymentMethod', v)}
              title="Card Auto-Pay"
              blurb="Card saved once and charged automatically each month. Credit cards include a ~3.3% processing fee on each invoice; debit cards are charged at face value."
            />
            <PaymentMethodOption
              value="card_invoice"
              current={form.paymentMethod}
              onChange={(v) => set('paymentMethod', v)}
              title="Card Invoice (Pay button)"
              blurb="We email a monthly invoice with a Pay button. The customer enters their card the first time and pays in one tap from then on. Credit-card processing fee passes through."
            />
            <PaymentMethodOption
              value="manual"
              current={form.paymentMethod}
              onChange={(v) => set('paymentMethod', v)}
              title="Manual / Check"
              blurb="No auto-pay. Customer receives an invoice email and pays by check or other means."
            />
          </div>
        </Field>
        <Field label="Notes">
          <textarea className="input min-h-[80px]" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        {err ? <p className="text-sm text-red-600">{err}</p> : null}
        <div className="flex gap-2 justify-end pt-1">
          <Link to="/customers" className="btn-ghost">Cancel</Link>
          <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create Customer'}</button>
        </div>
      </form>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  )
}

function PaymentMethodOption({ value, current, onChange, title, blurb }) {
  const checked = current === value
  return (
    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
      checked ? 'border-brand-green-deep bg-brand-mint/50' : 'border-gray-200 hover:border-gray-300'
    }`}>
      <input
        type="radio"
        name="paymentMethod"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <span className="flex-1">
        <span className="block font-bold">{title}</span>
        <span className="block text-xs text-gray-600 mt-0.5">{blurb}</span>
      </span>
    </label>
  )
}
